import {useEffect, useState} from 'react';
import {Box, Typography, IconButton, CircularProgress, useMediaQuery, GlobalStyles} from '@mui/material';
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {useQrScanner} from '@/utils/hooks';
import {ReactComponent as CheckedErrorIcon} from '@icons/close_big.svg';

const QR_REGION_ID = 'qr-video-container';

const cornerStyle = {
	position: 'absolute',
	width: '44px',
	height: '44px',
	borderColor: '#00b0ff',
	borderStyle: 'solid',
	zIndex: 2,
};

interface ScanPassScreenProps {
	open: boolean;
	onClose: () => void;
	onCheckInComplete?: (guestId: string) => Promise<void>;
}

export const ScanPassScreen = ({open, onClose, onCheckInComplete}: ScanPassScreenProps) => {
	// Fix: drive isCameraMode from the open prop so the scanner only
	// starts when the screen is actually visible
	const [isCameraMode, setIsCameraMode] = useState(false);

	const isLaptop = useMediaQuery('(min-width:1200px)');

	useEffect(() => {
		if (open) {
			setIsCameraMode(true);
		} else {
			// When closed, turn off camera mode so the cleanup in useQrScanner fires
			setIsCameraMode(false);
		}
	}, [open]);

	const {isScannerReady, scanError, resumeScanner} = useQrScanner({
		qrcodeRegionId: QR_REGION_ID,
		isCameraMode,
		onScanSuccess: async (decodedText: string) => {
			try {
				await onCheckInComplete?.(decodedText);
			} finally {
				// Resume scanning after check-in attempt (success or error)
				resumeScanner();
			}
		},
	});

	// Fix: don't render anything while closed — avoids occupying the viewport
	if (!open) return null;

	return (
		<>
			{/* ✅ Глобальні стилі для html5-qrcode елементів */}
			<GlobalStyles
				styles={{
					[`#${QR_REGION_ID}`]: {
						width: '100% !important',
						height: '100% !important',
					},
					[`#${QR_REGION_ID} video`]: {
						width: '100% !important',
						height: '100% !important',
						objectFit: 'cover !important',
					},
					[`#${QR_REGION_ID} img`]: {
						width: '100% !important',
						height: '100% !important',
						objectFit: 'cover !important',
					},
					// Прибирає зайвий div з розмірами від бібліотеки
					[`#${QR_REGION_ID} > div`]: {
						width: '100% !important',
						height: '100% !important',
					},
				}}
			/>

			<Box
				sx={{
					position: 'fixed',
					inset: 0,
					width: '100%',
					height: '100vh',
					bgcolor: '#000',
					overflow: 'hidden',
					zIndex: 1300,
				}}
			>
				{/* Top blue header bar */}
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						height: '60px',
						bgcolor: {xs: '#00b0ff', lg: 'transparent'},
						display: 'flex',
						alignItems: 'center',
						px: 2,
						zIndex: 10,
					}}
				>
					<IconButton onClick={onClose} sx={{color: '#fff'}}>
						<CheckedErrorIcon />
					</IconButton>
					<Typography variant="h6" sx={{color: '#fff', width: '100%', textAlign: 'center', mr: 4, fontWeight: 500}}>
						Scan Pass
					</Typography>
				</Box>

				{/* ✅ Контейнер камери — розтягнутий на весь екран мінус хедер */}
				<Box
					id={QR_REGION_ID}
					sx={{
						width: '100%',
						height: 'calc(100vh - 60px)',
						mt: '60px',
						position: 'relative',
					}}
				/>

				{/* Loading */}
				{!isScannerReady && !scanError && (
					<Box
						sx={{
							position: 'absolute',
							inset: 0,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 2,
							pt: '60px',
							zIndex: 5,
						}}
					>
						<CircularProgress sx={{color: '#00b0ff'}} />
						<Typography sx={{color: '#fff', fontSize: 14}}>Starting camera…</Typography>
					</Box>
				)}

				{/* Error */}
				{scanError && (
					<Box
						sx={{
							position: 'absolute',
							inset: 0,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							pt: '60px',
							px: 3,
							zIndex: 5,
						}}
					>
						<Typography sx={{color: '#ff4d4d', textAlign: 'center', fontSize: 15}}>{scanError}</Typography>
					</Box>
				)}
			</Box>
		</>
	);
};
